import React from "react";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import Divider from "@material-ui/core/Divider";

import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import WabaTemplatePreview from "../WabaTemplateModal/WabaTemplatePreview";
import { useWabaTemplateCreate } from "./useWabaTemplateCreate";

const LANGUAGES = [
  { value: "es_PE", label: "Español (Perú)" },
  { value: "es_ES", label: "Español (España)" },
  { value: "es_MX", label: "Español (México)" },
  { value: "es_AR", label: "Español (Argentina)" },
  { value: "en_US", label: "Inglés (EE.UU.)" },
  { value: "pt_BR", label: "Portugués (Brasil)" },
];

const CATEGORIES = [
  { value: "MARKETING", label: "Marketing" },
  { value: "UTILITY", label: "Utilidad" },
];

const BUTTON_TYPES = [
  { value: "QUICK_REPLY", label: "Respuesta rápida" },
  { value: "URL", label: "Enlace (URL)" },
  { value: "PHONE_NUMBER", label: "Teléfono" },
];

const useStyles = makeStyles((theme) => ({
  sectionLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
  field: { marginBottom: theme.spacing(1.5) },
  buttonRow: { display: "flex", gap: theme.spacing(1), alignItems: "flex-start", marginBottom: theme.spacing(1) },
  previewLabel: { fontSize: "0.75rem", color: theme.palette.text.secondary, marginBottom: 6 },
  errorText: { color: theme.palette.error.main, fontSize: "0.8rem" },
}));

export default function WabaTemplateCreateModal({ open, onClose, wabaConnections = [], onCreated }) {
  const classes = useStyles();
  const {
    form,
    set,
    setHeaderType,
    handleUploadHeader,
    setExample,
    headerVarCount,
    bodyVarCount,
    addButton,
    setButton,
    removeButton,
    preview,
    error,
    saving,
    handleSubmit,
  } = useWabaTemplateCreate({ open, wabaConnections, onCreated, onClose });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Crear plantilla de Meta</DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* ── Formulario ── */}
          <Grid item xs={12} md={7}>
            <FormControl fullWidth variant="outlined" size="small" className={classes.field}>
              <InputLabel>Conexión WABA *</InputLabel>
              <Select
                label="Conexión WABA *"
                value={form.whatsappId}
                onChange={(e) => set("whatsappId", e.target.value)}
              >
                {wabaConnections.map((w) => (
                  <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              className={classes.field}
              fullWidth size="small" variant="outlined"
              label="Nombre *"
              value={form.name}
              onChange={(e) => set("name", e.target.value.toLowerCase())}
              helperText="Solo minúsculas, números y guiones bajos. Ej: confirmacion_pedido"
              inputProps={{ maxLength: 512 }}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth variant="outlined" size="small" className={classes.field}>
                  <InputLabel>Categoría *</InputLabel>
                  <Select label="Categoría *" value={form.category} onChange={(e) => set("category", e.target.value)}>
                    {CATEGORIES.map((c) => (
                      <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth variant="outlined" size="small" className={classes.field}>
                  <InputLabel>Idioma *</InputLabel>
                  <Select label="Idioma *" value={form.language} onChange={(e) => set("language", e.target.value)}>
                    {LANGUAGES.map((l) => (
                      <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Encabezado */}
            <div className={classes.sectionLabel}>Encabezado (opcional)</div>
            <FormControl fullWidth variant="outlined" size="small" className={classes.field}>
              <InputLabel>Tipo de encabezado</InputLabel>
              <Select label="Tipo de encabezado" value={form.headerType} onChange={(e) => setHeaderType(e.target.value)}>
                <MenuItem value="NONE">Ninguno</MenuItem>
                <MenuItem value="TEXT">Texto</MenuItem>
                <MenuItem value="IMAGE">Imagen</MenuItem>
              </Select>
            </FormControl>
            {form.headerType === "IMAGE" && (
              <div className={classes.field}>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  disabled={form.uploadingHeader || !form.whatsappId}
                >
                  {form.uploadingHeader ? "Subiendo..." : "Subir imagen"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    hidden
                    onChange={(e) => handleUploadHeader(e.target.files?.[0])}
                  />
                </Button>
                {form.uploadingHeader && (
                  <CircularProgress size={16} style={{ marginLeft: 8, verticalAlign: "middle" }} />
                )}
                {form.headerHandle && !form.uploadingHeader && (
                  <Typography variant="caption" style={{ color: "#1b5e20", marginLeft: 8 }}>
                    Imagen lista ✓
                  </Typography>
                )}
                {!form.whatsappId && (
                  <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
                    Selecciona primero la conexión WABA.
                  </Typography>
                )}
              </div>
            )}
            {form.headerType === "TEXT" && (
              <>
                <TextField
                  className={classes.field}
                  fullWidth size="small" variant="outlined"
                  label="Texto del encabezado"
                  value={form.headerText}
                  onChange={(e) => set("headerText", e.target.value)}
                  helperText="Puedes usar {{1}} para una variable."
                  inputProps={{ maxLength: 60 }}
                />
                {Array.from({ length: headerVarCount }).map((_, i) => (
                  <TextField
                    key={`he-${i}`}
                    className={classes.field}
                    fullWidth size="small" variant="outlined"
                    label={`Ejemplo del encabezado {{${i + 1}}}`}
                    value={form.headerExamples[i] || ""}
                    onChange={(e) => setExample("headerExamples", i, e.target.value)}
                  />
                ))}
              </>
            )}

            {/* Cuerpo */}
            <div className={classes.sectionLabel}>Cuerpo *</div>
            <TextField
              className={classes.field}
              fullWidth multiline rows={4} size="small" variant="outlined"
              label="Cuerpo del mensaje *"
              value={form.body}
              onChange={(e) => set("body", e.target.value)}
              helperText="Usa {{1}}, {{2}}… para variables (secuenciales)."
              inputProps={{ maxLength: 1024 }}
            />
            {Array.from({ length: bodyVarCount }).map((_, i) => (
              <TextField
                key={`be-${i}`}
                className={classes.field}
                fullWidth size="small" variant="outlined"
                label={`Ejemplo del cuerpo {{${i + 1}}}`}
                value={form.bodyExamples[i] || ""}
                onChange={(e) => setExample("bodyExamples", i, e.target.value)}
              />
            ))}

            {/* Pie */}
            <div className={classes.sectionLabel}>Pie (opcional)</div>
            <TextField
              className={classes.field}
              fullWidth size="small" variant="outlined"
              label="Texto del pie"
              value={form.footer}
              onChange={(e) => set("footer", e.target.value)}
              inputProps={{ maxLength: 60 }}
            />

            {/* Botones */}
            <div className={classes.sectionLabel}>Botones (opcional)</div>
            {form.buttons.map((b, i) => (
              <div key={`btn-${i}`} className={classes.buttonRow}>
                <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
                  <InputLabel>Tipo</InputLabel>
                  <Select label="Tipo" value={b.type} onChange={(e) => setButton(i, "type", e.target.value)}>
                    {BUTTON_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small" variant="outlined" label="Texto"
                  value={b.text} onChange={(e) => setButton(i, "text", e.target.value)}
                  style={{ flex: 1 }} inputProps={{ maxLength: 25 }}
                />
                {b.type === "URL" && (
                  <TextField
                    size="small" variant="outlined" label="URL"
                    value={b.url} onChange={(e) => setButton(i, "url", e.target.value)}
                    style={{ flex: 1 }} placeholder="https://..."
                  />
                )}
                {b.type === "PHONE_NUMBER" && (
                  <TextField
                    size="small" variant="outlined" label="Teléfono"
                    value={b.phone} onChange={(e) => setButton(i, "phone", e.target.value)}
                    style={{ flex: 1 }} placeholder="+51999..."
                  />
                )}
                <IconButton size="small" onClick={() => removeButton(i)} title="Quitar botón">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
            {form.buttons.length < 3 && (
              <Button size="small" startIcon={<AddIcon />} onClick={addButton}>
                Agregar botón
              </Button>
            )}
          </Grid>

          {/* ── Preview ── */}
          <Grid item xs={12} md={5}>
            <Divider style={{ marginBottom: 12 }} />
            <div className={classes.previewLabel}>Vista previa (como la verá el cliente)</div>
            <WabaTemplatePreview
              parsed={preview.parsed}
              headerMediaUrl={preview.headerMediaUrl}
              headerTextVars={preview.headerTextVars}
              bodyVars={preview.bodyVars}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        {error && <span className={classes.errorText} style={{ marginRight: "auto", paddingLeft: 8 }}>{error}</span>}
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={saving || !!error}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? "Enviando..." : "Crear y enviar a Meta"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
